import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = ({ value, onChange, minDate, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const wrapperRef = useRef(null);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  // Cerrar cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Ajustar para que la semana empiece en lunes (0 = domingo, 1 = lunes)
    let startDay = firstDay.getDay() - 1;
    if (startDay === -1) startDay = 6; // Si es domingo, poner al final

    const days = [];
    
    // Días del mes anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }
    
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }
    
    // Días del mes siguiente
    const remainingDays = 42 - days.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateClick = (dateObj) => {
    if (!dateObj.isCurrentMonth) {
      // Si se hace click en día de otro mes, cambiar al mes correspondiente
      setCurrentMonth(new Date(dateObj.date));
      return;
    }

    const minDateObj = minDate ? new Date(minDate) : null;
    if (minDateObj && dateObj.date < minDateObj) {
      return; // No permitir seleccionar fechas anteriores a minDate
    }

    setSelectedDate(dateObj.date);
    // Formatear fecha usando componentes locales para evitar problemas de timezone
    const year = dateObj.date.getFullYear();
    const month = String(dateObj.date.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    onChange(formattedDate);
    setIsOpen(false);
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const isPastDate = (date) => {
    const minDateObj = minDate ? new Date(minDate) : null;
    if (!minDateObj) return false;
    return date < minDateObj;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {/* Input */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between cursor-pointer hover:border-duetto-primary transition-colors bg-white"
      >
        <span className={selectedDate ? 'text-gray-900' : 'text-gray-400'}>
          {selectedDate ? formatDisplayDate(selectedDate) : 'Selecciona una fecha'}
        </span>
        <Calendar className="w-5 h-5 text-gray-400" />
      </div>

      {/* Calendario Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-72">
          {/* Header del calendario */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 rounded-full transition"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            
            <div className="text-center">
              <div className="text-base font-bold text-gray-900">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </div>
            </div>
            
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 rounded-full transition"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Nombres de los días */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {dayNames.map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Días del calendario */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((dateObj, index) => {
              const isSelectedDay = isSelected(dateObj.date);
              const isTodayDay = isToday(dateObj.date);
              const isPast = isPastDate(dateObj.date);
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateClick(dateObj)}
                  disabled={isPast}
                  className={`
                    aspect-square flex items-center justify-center text-sm rounded-lg transition-all
                    ${!dateObj.isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                    ${isSelectedDay 
                      ? 'bg-duetto-primary text-white font-bold shadow-md' 
                      : isTodayDay 
                        ? 'bg-duetto-primary bg-opacity-10 text-duetto-primary font-semibold border border-duetto-primary'
                        : isPast
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'hover:bg-gray-100'
                    }
                    ${dateObj.isCurrentMonth && !isPast ? 'cursor-pointer' : ''}
                  `}
                >
                  {dateObj.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
